import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthResponse } from '@/shared/types/auth';
import { authApi } from '@/shared/api/auth';
import { hasPermission, type Permission } from '@/shared/types/permissions';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string) => void;
  login: (response: AuthResponse) => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
  initialize: () => Promise<void>;

  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  canManageMembers: () => boolean;
  canManageSources: () => boolean;
  canManageProjects: () => boolean;
  canManageBilling: () => boolean;
}

/**
 * Auth store using Zustand with persistence.
 * Manages user authentication state and tokens.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => {
        set({ user, isAuthenticated: true });
      },

      setTokens: (accessToken) => {
        set({ accessToken });
      },

      login: (response) => {
        set({
          user: response.user,
          accessToken: response.access_token,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear localStorage
        localStorage.removeItem('auth-storage');
      },

      refreshToken: async () => {
        try {
          const response = await authApi.refresh();
          set({ accessToken: response.access_token });
        } catch (error) {
          console.error('Token refresh failed:', error);
          get().logout();
          throw error;
        }
      },

      initialize: async () => {
        const token = get().accessToken;

        if (!token) {
          set({ isLoading: false });
          return;
        }

        try {
          // Verify token by fetching user profile
          const user = await authApi.me();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          // Try to refresh token
          try {
            await get().refreshToken();
            const user = await authApi.me();
            set({ user, isAuthenticated: true, isLoading: false });
          } catch {
            get().logout();
          }
        }
      },

      hasPermission: (permission) => {
        const user = get().user;
        if (!user) return false;
        return hasPermission(user.role, permission);
      },

      canManageMembers: () => {
        return get().hasPermission('manage_members');
      },

      canManageSources: () => {
        return get().hasPermission('manage_sources');
      },

      canManageProjects: () => {
        return get().hasPermission('manage_projects');
      },

      canManageBilling: () => {
        return get().hasPermission('manage_billing');
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
      }),
    }
  )
);
